import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useToastStore } from '../store/toastStore';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { FormField } from '../components/ui/FormField';
import { Input } from '../components/ui/Input';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import { Pagination } from '../components/ui/Pagination';
import { Spinner } from '../components/ui/Spinner';
import { useInitEffect } from '../hooks/useInitEffect';
import { useAdminProductList, ADMIN_PRODUCT_SORT_OPTIONS } from '../hooks/useAdminProductList';
import { adminService } from '../services/adminService';
import { StoreSellerAdmin, CreateStoreSellerRequest, UserByRole, AdminCategory, AdminPriceTable, UpdateCategoryRequest, AdminProductStatus, AdminProduct } from '../models';
import { AdminProductImage } from '../models/product';
import { formatPhoneMask, unmaskPhone } from '../utils/phone';
import { formatCurrency } from '../utils/currency';

// ---------------------------------------------------------------------------
// SectionHeader
// ---------------------------------------------------------------------------

function SectionHeader({ title, description }: { title: string; description: string }) {
  return (
    <div className="mb-4">
      <h2 className="text-lg font-semibold text-text-dark">{title}</h2>
      <p className="text-sm text-text-light mt-0.5">{description}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CategoryPriceTableSection
// ---------------------------------------------------------------------------

function CategoryPriceTableSection() {
  const showToast = useToastStore((s) => s.show);
  const [categories, setCategories] = useState<AdminCategory[]>([]);
  const [priceTables, setPriceTables] = useState<AdminPriceTable[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const [bulkPriceTableId, setBulkPriceTableId] = useState<number | undefined>(undefined);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [catResult, ptResult] = await Promise.all([
        adminService.getCategories(),
        adminService.getPriceTables(),
      ]);
      setCategories(catResult.items);
      setPriceTables(ptResult.items);
    } catch {
      showToast('Erro ao carregar categorias e tabelas de preço.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useInitEffect(() => {
    loadData();
  });

  const filteredCategories = useMemo(() => {
    if (!categorySearch.trim()) return categories;
    return categories.filter((cat) =>
      cat.name.toLowerCase().includes(categorySearch.toLowerCase())
    );
  }, [categories, categorySearch]);

  const priceTableOptions = priceTables.map((pt) => ({
    id: pt.id,
    label: pt.isDefault ? `${pt.name} (Padrão)` : pt.name,
  }));

  const handlePriceTableChange = async (category: AdminCategory, priceTableId: number | undefined) => {
    const newPriceTableId = priceTableId ?? null;
    if (newPriceTableId === category.priceTableId) return;

    setSavingId(category.id);
    const payload: UpdateCategoryRequest = {
      name: category.name,
      description: category.description,
      active: category.active,
      priceTableId: newPriceTableId,
    };

    try {
      await adminService.updateCategory(category.id, payload);
      setCategories((prev) =>
        prev.map((c) =>
          c.id === category.id
            ? {
                ...c,
                priceTableId: newPriceTableId,
                priceTableName: priceTables.find((pt) => pt.id === newPriceTableId)?.name ?? null,
              }
            : c
        )
      );
      showToast('Tabela de preço atualizada com sucesso.', 'success');
    } catch (err: any) {
      const msg =
        err?.response?.data?.errors?.[0] ??
        err?.response?.data?.message ??
        'Erro ao atualizar tabela de preço.';
      showToast(msg, 'error');
    } finally {
      setSavingId(null);
    }
  };

  const defaultPriceTable = priceTables.find((pt) => pt.isDefault);

  const handleSetDefault = async () => {
    if (!defaultPriceTable) {
      showToast('Nenhuma tabela de preço está marcada como padrão.', 'error');
      return;
    }
    if (!window.confirm(`Definir "${defaultPriceTable.name}" como tabela de preço para todas as categorias?`)) return;

    setBulkSaving(true);
    try {
      await Promise.all(
        categories.map((cat) =>
          adminService.updateCategory(cat.id, {
            name: cat.name,
            description: cat.description,
            active: cat.active,
            priceTableId: defaultPriceTable.id,
          })
        )
      );
      setCategories((prev) =>
        prev.map((c) => ({ ...c, priceTableId: defaultPriceTable.id, priceTableName: defaultPriceTable.name }))
      );
      showToast(`Tabela "${defaultPriceTable.name}" aplicada a todas as categorias.`, 'success');
    } catch {
      showToast('Erro ao aplicar tabela padrão.', 'error');
    } finally {
      setBulkSaving(false);
    }
  };

  const handleApplyToAll = async () => {
    if (bulkPriceTableId === undefined) {
      showToast('Selecione uma tabela de preço primeiro.', 'error');
      return;
    }
    const selected = priceTables.find((pt) => pt.id === bulkPriceTableId);
    if (!selected) return;
    if (!window.confirm(`Aplicar "${selected.name}" para todas as categorias?`)) return;

    setBulkSaving(true);
    try {
      await Promise.all(
        categories.map((cat) =>
          adminService.updateCategory(cat.id, {
            name: cat.name,
            description: cat.description,
            active: cat.active,
            priceTableId: selected.id,
          })
        )
      );
      setCategories((prev) =>
        prev.map((c) => ({ ...c, priceTableId: selected.id, priceTableName: selected.name }))
      );
      showToast(`Tabela "${selected.name}" aplicada a todas as categorias.`, 'success');
    } catch {
      showToast('Erro ao aplicar tabela de preço.', 'error');
    } finally {
      setBulkSaving(false);
    }
  };

  return (
    <section className="bg-white rounded-2xl shadow-sm p-6">
      <SectionHeader
        title="Tabela de Preço por Categoria"
        description="Configure qual tabela de preço será usada para exibir os preços de cada categoria na loja."
      />

      {/* Ações em lote */}
      {!loading && categories.length > 0 && (
        <div className="mb-5 p-4 bg-champagne-light rounded-xl border border-champagne">
          <p className="text-xs font-medium text-text-medium mb-3">Ações em lote</p>
          <div className="flex flex-col sm:flex-row gap-3">
            {defaultPriceTable && (
              <Button
                size="sm"
                variant="secondary"
                onClick={handleSetDefault}
                disabled={bulkSaving}
                loading={bulkSaving}
              >
                Aplicar padrão ({defaultPriceTable.name}) para todas
              </Button>
            )}
            <div className="flex items-center gap-2 flex-1">
              <div className="flex-1 max-w-xs">
                <SearchableSelect
                  options={priceTableOptions}
                  value={bulkPriceTableId}
                  onChange={(id) => setBulkPriceTableId(id)}
                  placeholder="Selecionar tabela..."
                  allLabel="Nenhuma"
                />
              </div>
              <Button
                size="sm"
                onClick={handleApplyToAll}
                disabled={bulkSaving || bulkPriceTableId === undefined}
                loading={bulkSaving}
              >
                Aplicar para todas
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="relative mb-4">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-light pointer-events-none"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
        </svg>
        <input
          type="text"
          placeholder="Filtrar por nome da categoria..."
          value={categorySearch}
          onChange={(e) => setCategorySearch(e.target.value)}
          className="w-full pl-9 pr-8 py-2 rounded-lg border border-champagne-dark text-text-dark placeholder-text-light bg-white focus:outline-none focus:ring-2 focus:ring-chocolate/20 focus:border-chocolate transition-colors text-sm"
        />
        {categorySearch && (
          <button
            onClick={() => setCategorySearch('')}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-light hover:text-chocolate transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      <div className="border border-champagne rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-champagne text-text-medium">
              <th className="text-left px-4 py-3 font-medium">Categoria</th>
              <th className="text-left px-4 py-3 font-medium">Tabela de Preço</th>
              <th className="text-left px-4 py-3 font-medium hidden sm:table-cell">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={3} className="text-center py-12 text-text-light text-sm">
                  Carregando...
                </td>
              </tr>
            ) : filteredCategories.length === 0 ? (
              <tr>
                <td colSpan={3}>
                  <EmptyState
                    title={categorySearch ? 'Nenhuma categoria encontrada' : 'Nenhuma categoria cadastrada'}
                    description={categorySearch ? 'Tente buscar com outro termo.' : 'Cadastre categorias no painel administrativo para configurar as tabelas de preço.'}
                  />
                </td>
              </tr>
            ) : (
              filteredCategories.map((category) => (
                <tr
                  key={category.id}
                  className="border-t border-champagne hover:bg-champagne-light/50 transition-colors"
                >
                  <td className="px-4 py-3 font-medium text-text-dark">{category.name}</td>
                  <td className="px-4 py-3">
                    <div className="max-w-xs">
                      <SearchableSelect
                        options={priceTableOptions}
                        value={category.priceTableId ?? undefined}
                        onChange={(id) => handlePriceTableChange(category, id)}
                        placeholder={savingId === category.id ? 'Salvando...' : 'Nenhuma tabela'}
                        allLabel="Nenhuma tabela"
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        category.priceTableId
                          ? 'bg-success/10 text-success'
                          : 'bg-champagne text-text-light'
                      }`}
                    >
                      {category.priceTableId ? 'Configurada' : 'Sem tabela'}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// SellersSection
// ---------------------------------------------------------------------------

const SELLER_ROLE_ID = 2;

function SellersSection() {
  const showToast = useToastStore((s) => s.show);
  const [sellers, setSellers] = useState<StoreSellerAdmin[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [sellerUsers, setSellerUsers] = useState<UserByRole[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>(undefined);
  const [whatsAppPhone, setWhatsAppPhone] = useState('');
  const [saving, setSaving] = useState(false);

  const loadSellers = useCallback(async () => {
    setLoading(true);
    try {
      const result = await adminService.getSellers({ page: 1, pageSize: 50 });
      setSellers(result.items);
    } catch (err) {
      console.error('Erro ao carregar vendedores:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useInitEffect(() => {
    loadSellers();
    adminService.getUsersByRole(SELLER_ROLE_ID)
      .then(setSellerUsers)
      .catch(() => {});
  });

  const handleToggleForm = () => {
    setShowForm((prev) => !prev);
    setSelectedUserId(undefined);
    setWhatsAppPhone('');
  };

  const handleUserSelect = (id: number | undefined) => {
    setSelectedUserId(id);
    if (id !== undefined) {
      const user = sellerUsers.find((u) => u.id === id);
      if (user?.phone) setWhatsAppPhone(formatPhoneMask(user.phone));
    } else {
      setWhatsAppPhone('');
    }
  };

  const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedUserId || !whatsAppPhone.trim()) return;

    setSaving(true);
    try {
      await adminService.createSeller({
        userId: selectedUserId,
        whatsAppPhone: unmaskPhone(whatsAppPhone),
      });
      showToast('Vendedor adicionado com sucesso.', 'success');
      setShowForm(false);
      setSelectedUserId(undefined);
      setWhatsAppPhone('');
      await loadSellers();
    } catch (err: any) {
      const msg = err?.response?.data?.errors?.[0] ?? err?.response?.data?.message ?? 'Erro ao salvar vendedor.';
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await adminService.deleteSeller(id);
      setSellers((prev) => prev.filter((s) => s.id !== id));
      showToast('Vendedor removido com sucesso.', 'success');
    } catch (err: any) {
      const msg = err?.response?.data?.errors?.[0] ?? err?.response?.data?.message ?? 'Erro ao remover vendedor.';
      showToast(msg, 'error');
    }
  };

  return (
    <section className="bg-white rounded-2xl shadow-sm p-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <SectionHeader
          title="Vendedores"
          description="Controle quais vendedores têm acesso ao sistema da loja."
        />
        <Button size="sm" onClick={handleToggleForm}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.75}
              d={showForm ? 'M6 18L18 6M6 6l12 12' : 'M12 4v16m8-8H4'}
            />
          </svg>
          {showForm ? 'Cancelar' : 'Adicionar vendedor'}
        </Button>
      </div>

      {/* Inline creation form */}
      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="mb-5 p-4 bg-champagne-light rounded-xl border border-champagne flex flex-col sm:flex-row gap-3 items-end"
        >
          <FormField label="Vendedor" required>
            <SearchableSelect
              options={sellerUsers.map((u) => ({ id: u.id, label: `${u.name} (${u.email})` }))}
              value={selectedUserId}
              onChange={handleUserSelect}
              placeholder="Selecionar vendedor..."
              allLabel="Nenhum"
            />
          </FormField>
          <FormField label="WhatsApp" required>
            <Input
              type="tel"
              placeholder="(85) 99922-4455"
              value={whatsAppPhone}
              onChange={(e) => setWhatsAppPhone(formatPhoneMask(e.target.value))}
            />
          </FormField>
          <div className="flex gap-2 shrink-0 pb-0.5">
            <Button type="submit" size="sm" loading={saving}>
              Salvar
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={handleToggleForm}>
              Cancelar
            </Button>
          </div>
        </form>
      )}

      {/* Table */}
      <div className="border border-champagne rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-champagne text-text-medium">
              <th className="text-left px-4 py-3 font-medium">Nome</th>
              <th className="text-left px-4 py-3 font-medium hidden sm:table-cell">E-mail</th>
              <th className="text-left px-4 py-3 font-medium hidden md:table-cell">WhatsApp</th>
              <th className="text-left px-4 py-3 font-medium">Ativo</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="text-center py-12 text-text-light text-sm">
                  Carregando...
                </td>
              </tr>
            ) : sellers.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <EmptyState
                    title="Nenhum vendedor cadastrado"
                    description="Adicione vendedores para que possam acessar o painel da loja."
                  />
                </td>
              </tr>
            ) : (
              sellers.map((seller) => (
                <tr
                  key={seller.id}
                  className="border-t border-champagne hover:bg-champagne-light/50 transition-colors"
                >
                  <td className="px-4 py-3 font-medium text-text-dark">{seller.userName}</td>
                  <td className="px-4 py-3 text-text-medium hidden sm:table-cell">
                    {seller.userEmail}
                  </td>
                  <td className="px-4 py-3 text-text-medium hidden md:table-cell">
                    {seller.whatsAppPhone}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        seller.active
                          ? 'bg-success/10 text-success'
                          : 'bg-champagne text-text-light'
                      }`}
                    >
                      {seller.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="danger" size="sm" onClick={() => handleDelete(seller.id)}>
                      Remover
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// ProductImagesModal
// ---------------------------------------------------------------------------

interface ProductImagesModalProps {
  product: AdminProduct;
  onClose: () => void;
  onImagesChanged: (productId: number, images: AdminProductImage[]) => void;
}

function ProductImagesModal({ product, onClose, onImagesChanged }: ProductImagesModalProps) {
  const showToast = useToastStore((s) => s.show);
  const [images, setImages] = useState<AdminProductImage[]>(product.images ?? []);
  const [loadingImages, setLoadingImages] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onImagesChangedRef = useRef(onImagesChanged);
  onImagesChangedRef.current = onImagesChanged;

  const loadImages = useCallback(async () => {
    setLoadingImages(true);
    try {
      const data = await adminService.getProductImages(product.id);
      setImages(data);
      onImagesChangedRef.current(product.id, data);
    } catch {
      showToast('Erro ao carregar imagens.', 'error');
    } finally {
      setLoadingImages(false);
    }
  }, [product.id, showToast]);

  useEffect(() => {
    loadImages();
  }, [loadImages]);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploading(true);
    try {
      await adminService.uploadProductImage(product.id, file);
      await loadImages();
      showToast('Imagem enviada com sucesso.', 'success');
    } catch (err: any) {
      const msg = err?.response?.data?.errors?.[0] ?? err?.response?.data?.message ?? 'Erro ao fazer upload da imagem.';
      showToast(msg, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleSetMain = async (imageId: number) => {
    try {
      await adminService.setMainProductImage(product.id, imageId);
      await loadImages();
      showToast('Imagem principal definida.', 'success');
    } catch (err: any) {
      const msg = err?.response?.data?.errors?.[0] ?? err?.response?.data?.message ?? 'Erro ao definir imagem principal.';
      showToast(msg, 'error');
    }
  };

  const handleDeleteImage = async (imageId: number) => {
    try {
      await adminService.deleteProductImage(product.id, imageId);
      const updated = images.filter((img) => img.id !== imageId);
      setImages(updated);
      onImagesChangedRef.current(product.id, updated);
      showToast('Imagem removida.', 'success');
    } catch (err: any) {
      const msg = err?.response?.data?.errors?.[0] ?? err?.response?.data?.message ?? 'Erro ao remover imagem.';
      showToast(msg, 'error');
    }
  };

  const handleMove = async (index: number, direction: -1 | 1) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= images.length) return;
    const reordered = [...images];
    [reordered[index], reordered[newIndex]] = [reordered[newIndex], reordered[index]];
    const withOrder = reordered.map((img, i) => ({ ...img, order: i + 1 }));
    setImages(withOrder);
    try {
      await adminService.reorderProductImages(
        product.id,
        withOrder.map((img) => ({ imageId: img.id, order: img.order }))
      );
      onImagesChangedRef.current(product.id, withOrder);
    } catch {
      showToast('Erro ao reordenar imagens.', 'error');
      await loadImages();
    }
  };

  return (
    /* Overlay */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Card */}
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-champagne flex-shrink-0">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-text-dark truncate">{product.name}</h3>
            <p className="text-xs text-text-light mt-0.5 font-mono">{product.sku}</p>
          </div>
          <button
            onClick={onClose}
            className="text-text-light hover:text-chocolate transition-colors flex-shrink-0 mt-0.5"
            aria-label="Fechar"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loadingImages ? (
            <div className="flex justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : images.length === 0 ? (
            <EmptyState
              title="Nenhuma imagem"
              description="Faça upload para adicionar imagens a este produto."
            />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {images.map((img, index) => (
                <div
                  key={img.id}
                  className="relative group rounded-xl overflow-hidden border border-champagne bg-champagne-light aspect-square"
                >
                  <img src={img.imageUrl} alt="" className="w-full h-full object-cover" />
                  {img.isMain && (
                    <span className="absolute top-1.5 left-1.5 bg-chocolate text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                      Principal
                    </span>
                  )}
                  {/* Botões de ordem — canto superior direito */}
                  <div className="absolute top-1.5 right-1.5 z-10 flex flex-row gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      disabled={index === 0}
                      onClick={() => handleMove(index, -1)}
                      className="w-6 h-6 rounded bg-black/60 text-white flex items-center justify-center disabled:opacity-30 hover:bg-black/80 transition-colors"
                      title="Mover para esquerda"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      disabled={index === images.length - 1}
                      onClick={() => handleMove(index, 1)}
                      className="w-6 h-6 rounded bg-black/60 text-white flex items-center justify-center disabled:opacity-30 hover:bg-black/80 transition-colors"
                      title="Mover para direita"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-2">
                    {!img.isMain && (
                      <Button size="sm" variant="secondary" onClick={() => handleSetMain(img.id)}>
                        Definir principal
                      </Button>
                    )}
                    <Button size="sm" variant="danger" onClick={() => handleDeleteImage(img.id)}>
                      Remover
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-champagne flex-shrink-0">
          <span className="text-xs text-text-light">
            {images.length} {images.length === 1 ? 'imagem' : 'imagens'}
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>
              Fechar
            </Button>
            <Button
              size="sm"
              loading={uploading}
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1M12 12V4m0 0L8 8m4-4l4 4" />
              </svg>
              Fazer upload
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProductsSection
// ---------------------------------------------------------------------------

const STATUS_OPTIONS: { label: string; value: AdminProductStatus }[] = [
  { label: 'Todos', value: 'all' },
  { label: 'Ativos', value: 'active' },
  { label: 'Inativos', value: 'inactive' },
];

function ProductsSection() {
  const [categories, setCategories] = useState<AdminCategory[]>([]);
  const [imagesModalProduct, setImagesModalProduct] = useState<AdminProduct | null>(null);

  useInitEffect(() => {
    adminService.getCategories().then((data) => setCategories(data.items)).catch(() => {});
  });

  const {
    products,
    updateProductImages,
    total,
    totalPages,
    isLoading,
    error,
    page,
    searchInput,
    categoryId,
    status,
    sortIndex,
    setPage,
    handleSearchChange,
    handleCategoryChange,
    handleStatusChange,
    handleSortChange,
    clearFilters,
    hasFilters,
  } = useAdminProductList();


  const handleOpenImagesModal = useCallback((product: AdminProduct) => {
    setImagesModalProduct(product);
  }, []);

  const handleCloseImagesModal = useCallback(() => {
    setImagesModalProduct(null);
  }, []);

  const handleImagesChanged = useCallback(
    (productId: number, images: AdminProductImage[]) => {
      updateProductImages(productId, images);
      if (imagesModalProduct?.id === productId) {
        setImagesModalProduct((prev) => prev ? { ...prev, images } : prev);
      }
    },
    [updateProductImages, imagesModalProduct]
  );

  const categoryOptions = categories.map((c) => ({ id: c.id, label: c.name }));

  return (
    <>
    {imagesModalProduct && (
      <ProductImagesModal
        product={imagesModalProduct}
        onClose={handleCloseImagesModal}
        onImagesChanged={handleImagesChanged}
      />
    )}
    <section className="bg-white rounded-2xl shadow-sm p-6">
      <SectionHeader
        title="Produtos"
        description="Visualize e filtre o catálogo de produtos cadastrados no sistema."
      />

      {/* Filters bar */}
      <div className="flex flex-col gap-3 mb-5">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-light pointer-events-none"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
            <input
              type="text"
              placeholder="Buscar por nome ou SKU..."
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full pl-9 pr-8 py-2 rounded-lg border border-champagne-dark text-text-dark placeholder-text-light bg-white focus:outline-none focus:ring-2 focus:ring-chocolate/20 focus:border-chocolate transition-colors text-sm"
            />
            {searchInput && (
              <button
                onClick={() => handleSearchChange('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-light hover:text-chocolate transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Category */}
          <div className="sm:w-52">
            <SearchableSelect
              options={categoryOptions}
              value={categoryId}
              onChange={handleCategoryChange}
              placeholder="Categoria"
              allLabel="Todas as categorias"
            />
          </div>

          {/* Sort */}
          <div className="relative sm:w-48">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-light pointer-events-none"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h18M6 12h12M10 17h4" />
            </svg>
            <select
              value={sortIndex}
              onChange={handleSortChange}
              className="w-full pl-9 pr-4 py-2 rounded-lg border border-champagne-dark text-text-dark bg-white focus:outline-none focus:ring-2 focus:ring-chocolate/20 focus:border-chocolate transition-colors text-sm appearance-none cursor-pointer"
            >
              {ADMIN_PRODUCT_SORT_OPTIONS.map((opt, idx) => (
                <option key={opt.label} value={idx}>
                  {opt.label}
                </option>
              ))}
            </select>
            <svg
              className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-light pointer-events-none"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          {/* Status filter chips */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-text-light font-medium">Status:</span>
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => handleStatusChange(opt.value)}
                className={[
                  'px-3 py-1 rounded-full text-xs border transition-colors',
                  status === opt.value
                    ? 'bg-champagne border-chocolate text-chocolate font-medium'
                    : 'border-champagne-dark text-text-medium hover:border-chocolate/60 hover:text-text-dark',
                ].join(' ')}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Clear filters + count */}
          <div className="flex items-center gap-3">
            {!isLoading && (
              <span className="text-xs text-text-light">
                {total} {total === 1 ? 'produto' : 'produtos'}
              </span>
            )}
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1.5 px-3 py-1 rounded-lg border border-champagne-dark text-xs text-text-medium hover:text-chocolate hover:border-chocolate transition-colors whitespace-nowrap"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Limpar filtros
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="border border-champagne rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-champagne text-text-medium">
              <th className="text-left px-4 py-3 font-medium w-12 hidden sm:table-cell">Foto</th>
              <th className="text-left px-4 py-3 font-medium">Produto</th>
              <th className="text-left px-4 py-3 font-medium hidden md:table-cell">SKU</th>
              <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">Categoria</th>
              <th className="text-right px-4 py-3 font-medium">Preço</th>
              <th className="text-left px-4 py-3 font-medium hidden sm:table-cell">Estoque</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 w-10" />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} className="py-16 text-center">
                  <div className="flex justify-center">
                    <Spinner size="lg" />
                  </div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={8}>
                  <EmptyState title="Erro ao carregar produtos" description={error} />
                </td>
              </tr>
            ) : products.length === 0 ? (
              <tr>
                <td colSpan={8}>
                  <EmptyState
                    title="Nenhum produto encontrado"
                    description={hasFilters ? 'Tente ajustar os filtros.' : 'Nenhum produto cadastrado.'}
                  />
                </td>
              </tr>
            ) : (
              products.map((product) => (
                <tr
                  key={product.id}
                  className="border-t border-champagne hover:bg-champagne-light/50 transition-colors"
                >
                  {/* Thumbnail */}
                  <td className="px-4 py-3 hidden sm:table-cell">
                    {(() => {
                      const mainImg = product.images?.find(i => i.isMain)?.imageUrl ?? product.images?.[0]?.imageUrl;
                      return (
                        <div className="w-10 h-10 rounded-lg overflow-hidden bg-champagne-light flex-shrink-0">
                          {mainImg ? (
                            <img src={mainImg} alt={product.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <svg className="w-5 h-5 text-text-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                              </svg>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </td>

                  {/* Name */}
                  <td className="px-4 py-3 font-medium text-text-dark max-w-xs">
                    <span className="line-clamp-2">{product.name}</span>
                  </td>

                  {/* SKU */}
                  <td className="px-4 py-3 text-text-medium font-mono text-xs hidden md:table-cell">
                    {product.sku}
                  </td>

                  {/* Category */}
                  <td className="px-4 py-3 text-text-medium hidden lg:table-cell">
                    {product.categoryName}
                  </td>

                  {/* Price */}
                  <td className="px-4 py-3 text-right">
                    {(() => {
                      const defaultPrice = product.productPrices?.find(p => p.isDefault) ?? product.productPrices?.[0];
                      if (!defaultPrice) return <span className="text-text-light text-xs">—</span>;
                      return (
                        <div className="flex flex-col items-end">
                          {defaultPrice.promotionalPrice ? (
                            <>
                              <span className="text-xs text-text-light line-through">
                                R$ {formatCurrency(defaultPrice.price)}
                              </span>
                              <span className="font-semibold text-chocolate">
                                R$ {formatCurrency(defaultPrice.promotionalPrice)}
                              </span>
                            </>
                          ) : (
                            <span className="font-semibold text-text-dark">
                              R$ {formatCurrency(defaultPrice.price)}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </td>

                  {/* Stock */}
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        product.stockCount > 0
                          ? 'bg-success/10 text-success'
                          : 'bg-danger/10 text-danger'
                      }`}
                    >
                      {product.stockCount > 0 ? 'Em estoque' : 'Sem estoque'}
                    </span>
                  </td>

                  {/* Active status */}
                  <td className="px-4 py-3">
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        product.status === 1
                          ? 'bg-success/10 text-success'
                          : 'bg-champagne text-text-light'
                      }`}
                    >
                      {product.status === 1 ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleOpenImagesModal(product)}
                      title="Gerenciar imagens"
                      className="relative flex items-center justify-center w-8 h-8 rounded-lg text-text-light hover:text-chocolate hover:bg-champagne transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {(product.images?.length ?? 0) > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] flex items-center justify-center bg-chocolate text-white text-[9px] font-bold rounded-full px-0.5 leading-none">
                          {product.images.length}
                        </span>
                      )}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && totalPages > 1 && (
        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      )}
    </section>
    </>
  );
}

// ---------------------------------------------------------------------------
// SettingsPage
// ---------------------------------------------------------------------------

interface Tab {
  key: string;
  label: string;
  icon: React.ReactNode;
}

const TABS: Tab[] = [
  {
    key: 'products',
    label: 'Produtos',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    ),
  },
  {
    key: 'priceTable',
    label: 'Tabela de Preço',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    key: 'sellers',
    label: 'Vendedores',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
  },
];

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState<string>(TABS[0].key);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text-dark">Configurações</h1>
        <p className="text-text-light text-sm mt-1">Área restrita para administradores.</p>
      </div>

      <div className="border-b border-champagne mb-6">
        <nav className="flex gap-1 -mb-px overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.key
                  ? 'border-chocolate text-chocolate'
                  : 'border-transparent text-text-light hover:text-text-dark hover:border-champagne-dark'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'products' && <ProductsSection />}
      {activeTab === 'priceTable' && <CategoryPriceTableSection />}
      {activeTab === 'sellers' && <SellersSection />}
    </div>
  );
}
